import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider
} from 'react-router-dom';
import * as Sentry from '@sentry/react';

import ActivityListPage from '@/app/activity/ListActivitiesPage';
import AddActivityPage from '@/app/activity/AddActivityPage';
import EditActivityPage from '@/app/activity/EditActivityPage';
import LoginPage from '@/app/login/Page';
import RegionListPage from '@/app/region/ListPage';
import RegionViewPage from '@/app/region/ViewPage';
import TenantList from '@/app/tenant/List';
import TenantView from '@/app/tenant/View';
import ListOrdersPage from '@/app/order/ListOrdersPage';
import ViewOrderPage from '@/app/order/ViewOrderPage';
import SalesSiteLeadListPage from '@/app/salessiteLead/ListPage';
import CoachListPage from '@/app/coach/ListPage';
import CoachViewPage from '@/app/coach/ViewPage';
import CoachActivitiesNavigation from '@/app/coachActivity/Navigation';
import CoachActivitiesTab from '@/app/coachActivity/Tab';
import CoachViewTenantsTab from '@/app/coachActivityTenant/TenantsTab';
import CoachViewBookingsTab from '@/app/coach/components/view/BookingsTab';
import FeeEditPage from '@/app/fee/FeeEditPage';
import TaxListPage from '@/app/tax/TaxListPage';
import ErrorFallback from './ErrorFallback';
import HomePage from '@/app/home/HomePage';
import UserListPage from '@/app/user/UserListPage';
import UserViewPage from '@/app/user/UserViewPage';
import OfferingCategoryListPage from '@/app/offeringCategory/OfferingCategoryListPage';
import StepRouter from '@/app/coachActivity/StepRouter';

import App from './App.jsx';
import './index.css';

// Only initialize Sentry in production
if (import.meta.env.PROD) {
  // For Vite
  Sentry.init({
    dsn: 'https://51a2f04a5d53dc802e7ec3f6b3f642d4@o4508229293703168.ingest.us.sentry.io/4508229295669248',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration()
    ],
    // Tracing
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api.profoundpros\.com\/v1/
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Capture all errors
    sampleRate: 1.0,
    // Enable source maps
    enableTracing: true
  });
}

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <ErrorFallback />
  },
  {
    path: '/',
    element: <App />,
    errorElement: <ErrorFallback />,
    children: [
      {
        index: true,
        element: <Navigate to="/home" />
      },
      {
        path: 'activity',
        children: [
          {
            index: true,
            element: <ActivityListPage />
          },
          {
            path: 'add',
            element: <AddActivityPage />
          },
          {
            path: ':id',
            element: <EditActivityPage />
          }
        ]
      },
      {
        path: 'order',
        children: [
          {
            index: true,
            element: <ListOrdersPage />
          },
          {
            path: ':id',
            element: <ViewOrderPage />
          }
        ]
      },
      {
        path: 'user',
        children: [
          {
            index: true,
            element: <UserListPage />
          },
          {
            path: ':id',
            element: <UserViewPage />
          }
        ]
      },
      {
        path: 'coach',
        children: [
          {
            index: true,
            element: <CoachListPage />
          },
          {
            path: ':id',
            element: <CoachViewPage />,
            children: [
              {
                index: true,
                element: <Navigate to="activities" />
              },
              {
                path: 'activities',
                element: <CoachActivitiesNavigation />,
                children: [
                  {
                    path: ':coachActivityId',
                    element: <CoachActivitiesTab />,
                    children: [
                      {
                        index: true,
                        element: <Navigate to="personal-information" replace />
                      },
                      {
                        path: ':stepKey',
                        element: <StepRouter />
                      }
                    ]
                  }
                ]
              },
              {
                path: 'tenants',
                element: <CoachViewTenantsTab />
              },
              {
                path: 'bookings',
                element: <CoachViewBookingsTab />
              }
            ]
          }
        ]
      },
      {
        path: 'fee',
        children: [
          {
            index: true,
            element: <FeeEditPage />
          }
        ]
      },
      {
        path: 'tax',
        children: [
          {
            index: true,
            element: <TaxListPage />
          }
        ]
      },
      {
        path: 'home',
        children: [
          {
            index: true,
            element: <HomePage />
          }
        ]
      },
      {
        path: 'region',
        children: [
          {
            index: true,
            element: <RegionListPage />
          },
          {
            path: ':regionId',
            element: <RegionViewPage />
          }
        ]
      },
      {
        path: 'sales-site-leads',
        children: [
          {
            index: true,
            element: <SalesSiteLeadListPage />
          }
        ]
      },
      {
        path: 'tenant',
        children: [
          {
            index: true,
            element: <TenantList />
          },
          {
            path: ':tenantId',
            element: <TenantView />
          }
        ]
      },
      {
        path: 'offering-category',
        element: <OfferingCategoryListPage />
      }
    ]
  }
]);

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);
